/*  ─────────────────────────────────────────────────────────────────────────
    Site Settings · shared atoms & subsections
    Extracted from Site.jsx (was 2954 lines). This module owns the
    pure-render building blocks reused across tabs:
      · TYPEFACE_PAIRS, ACCENT_PRESETS, SUGGESTED_THEMES (config maps)
      · Section, Field, IconBtn (layout atoms)
      · CopyField (copy-to-clipboard input)
      · ImageUploader, HeroImageUploader, AssetUploader (asset upload UI)
      · TEMPLATE_BADGES + 4 miniatures + TEMPLATE_MINIATURES (live-preview engine)
      · BrandingLivePreview + AccentColorPicker (Branding tab visualizations)
      · PortfolioEditor + PortfolioTile (Portfolio tab list editor)
    Stateful subsections stay in Site.jsx; this file is intentionally pure
    so it can be unit-tested in isolation later.
    ───────────────────────────────────────────────────────────────────── */
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Upload, Loader2, X, Plus, Trash2, ImagePlus, RotateCcw, Sparkles, Copy,
  Check, RefreshCw, Link as LinkIcon, Monitor, Smartphone,
} from "lucide-react";
import { TEMPLATES } from "@/templates/registry";
import { THEMES, DEFAULT_THEME_ID, getThemeById, resolvePalette } from "@/lib/themes";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/* Tiny hex-lightness shifter used by the inline mock preview AND the shared
   miniature/preview atoms. Mirrors the helper in lib/themeApplier.js but
   returns a hex (since callers paint with raw hex via inline style). Exported
   so Site.jsx can reuse the same color math for its in-file previews. */
export function shiftHexLightness(hex, delta) {
  if (!hex || typeof hex !== "string") return hex;
  let h = hex.replace(/^#/, "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  if (!/^[0-9a-f]{6}$/i.test(h)) return hex;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const adjust = (c) => Math.min(255, Math.max(0, c + Math.round((delta / 100) * 255)));
  const next = [adjust(r), adjust(g), adjust(b)]
    .map((c) => c.toString(16).padStart(2, "0"))
    .join("");
  return `#${next}`;
}

/* Editorial typeface pairings exposed in the Branding tab. Cormorant + Manrope
   is the canonical Lumetier pair (already loaded via index.html link). The
   other two pairs are aspirational; their fallback stacks keep things tasteful
   until the real fonts are wired in (Phase 5+). */
export const TYPEFACE_PAIRS = [
  {
    id: "cormorant-manrope",
    serifLabel: "Cormorant Garamond",
    sansLabel: "Manrope",
    serif: "'Cormorant Garamond', 'EB Garamond', Georgia, serif",
    sans: "'Manrope', system-ui, -apple-system, 'Helvetica Neue', sans-serif",
  },
  {
    id: "canela-gtamerica",
    serifLabel: "Canela",
    sansLabel: "GT America",
    serif: "'Canela', 'Cormorant Garamond', 'Didot', 'Bodoni 72', serif",
    sans: "'GT America', 'Inter', -apple-system, system-ui, sans-serif",
  },
  {
    id: "ppeditorial-sohne",
    serifLabel: "PP Editorial New",
    sansLabel: "Söhne",
    serif: "'PP Editorial New', 'Cormorant Garamond', 'Times New Roman', serif",
    sans: "'Söhne', 'Söhne Buch', 'Inter', -apple-system, sans-serif",
  },
  // New pairs for the v2 template variants. Each is paired with one of the
  // 6 new templates by SUGGESTED_THEMES below, but is also pickable on its
  // own from the typeface dropdown.
  {
    id: "playfair-inter",
    serifLabel: "Playfair Display",
    sansLabel: "Inter",
    serif: "'Playfair Display', 'Cormorant Garamond', 'Didot', serif",
    sans: "'Inter', 'Manrope', -apple-system, system-ui, sans-serif",
  },
  {
    id: "fraunces-manrope",
    serifLabel: "Fraunces",
    sansLabel: "Manrope",
    serif: "'Fraunces', 'Cormorant Garamond', Georgia, serif",
    sans: "'Manrope', system-ui, -apple-system, sans-serif",
  },
  {
    id: "jetbrains-inter",
    serifLabel: "JetBrains Mono",
    sansLabel: "Inter",
    serif: "'JetBrains Mono', ui-monospace, 'SFMono-Regular', Menlo, monospace",
    sans: "'Inter', 'Manrope', -apple-system, system-ui, sans-serif",
  },
  {
    id: "dmserif-inter",
    serifLabel: "DM Serif Display",
    sansLabel: "Inter",
    serif: "'DM Serif Display', 'Playfair Display', 'Didot', serif",
    sans: "'Inter', 'Manrope', -apple-system, system-ui, sans-serif",
  },
];

export function Section({ title, subtitle, children }) {
  // Editorial two-column split row: heading + caption on the left (1/3),
  // form controls on the right (2/3). Generous py-12 spacing makes each
  // section feel like a separate page in a portfolio book.
  return (
    <section className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12 py-10 border-b border-border last:border-b-0">
      <div className="md:col-span-1">
        <h2 className="font-serif text-3xl sm:text-4xl italic font-light tracking-tight leading-tight">
          {title}
        </h2>
        {subtitle && (
          <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
            {subtitle}
          </p>
        )}
      </div>
      <div className="md:col-span-2">{children}</div>
    </section>
  );
}

export function Field({ label, descriptor, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-[0.32em] text-[color:var(--gold-base)] mb-2">{label}</span>
      {children}
      {descriptor && (
        <p className="mt-2 text-[11px] font-serif italic text-muted-foreground/70 leading-relaxed">
          {descriptor}
        </p>
      )}
    </label>
  );
}

/* ── BrandingLivePreview ──────────────────────────────────────────────────
   Memoized live-preview card. Receives only the primitive fields it needs
   (strings/hex) so React.memo's shallow compare skips renders unless one of
   those fields changes. Font-family is applied once via a CSS variable on
   the root rather than cascaded inline on multiple nested elements, so the
   browser repaints in a single pass per keystroke.

   Template badge ("EDITORIAL · cover scale" etc) gives the user a one-glance
   read on which template the public site will render in. */
export const TEMPLATE_BADGES = {
  editorial: { label: "EDITORIAL", note: "cover scale" },
  minimal:   { label: "MINIMAL",   note: "restrained" },
  bold:      { label: "BOLD",      note: "oversized" },
  journal:   { label: "JOURNAL",   note: "monograph" },
  // v2 — each gets a distinct badge so the Live Preview no longer
  // mislabels them as Editorial when picked. The `note` is a one-word
  // mnemonic the user can scan to know what they're getting.
  atelier:   { label: "ATELIER",   note: "intimate" },
  heirloom:  { label: "HEIRLOOM",  note: "archival" },
  vista:     { label: "VISTA",     note: "cinematic" },
  brutalist: { label: "BRUTALIST", note: "concrete" },
  spotlight: { label: "SPOTLIGHT", note: "theatrical" },
  botanical: { label: "BOTANICAL", note: "garden" },
};

/* Template-specific miniature renderers. Each receives the SAME props
   (palette, content, typography) and arranges them differently so a tenant
   switching templates in Site Settings sees the layout pivot — not just a
   relabeled badge. Kept deliberately lightweight: these are 220-300px wide
   editorial sketches, not pixel-faithful re-implementations of the public
   template bundles. The intent is structural recognition. */

export function EditorialMiniature({ resolved, pair, content, heroLine, portfolioThumbs }) {
  return (
    <>
      {/* Cinematic 16:9 hero with image + overlaid wordmark */}
      <div
        className="aspect-[16/9] flex items-center justify-center overflow-hidden relative"
        style={{ background: resolved.muted }}
      >
        {content.hero_image ? (
          <img src={content.hero_image} alt="" loading="lazy" decoding="async"
               className="absolute inset-0 w-full h-full object-cover opacity-80" />
        ) : null}
        {content.logo_url ? (
          <img src={content.logo_url} alt="" loading="lazy" decoding="async"
               className="relative max-h-12 max-w-[60%] object-contain opacity-95" />
        ) : (
          <span className="relative italic text-2xl" style={{ fontFamily: "var(--preview-serif)", opacity: 0.9 }}>
            {content.wordmark || content.studio_name || "Studio"}
          </span>
        )}
      </div>
      {/* Big italic serif headline + hairline rule */}
      <div>
        <div className="text-[10px] uppercase tracking-[0.32em] mb-1.5" style={{ color: resolved.accent }}>
          {content.hero_eyebrow || "ATELIER · 2026"}
        </div>
        <div className="text-2xl italic leading-tight" style={{ fontFamily: "var(--preview-serif)" }}>
          {heroLine}
        </div>
        <div className="mt-3 h-px w-10" style={{ background: resolved.accent }} />
      </div>
      {/* Asymmetric portfolio mosaic — first thumb big, others small */}
      {portfolioThumbs.length > 0 ? (
        <div className="grid grid-cols-3 gap-1.5">
          <div className="col-span-2 row-span-2 aspect-[4/5] overflow-hidden">
            <img src={portfolioThumbs[0]} alt="" loading="lazy" className="w-full h-full object-cover" />
          </div>
          {portfolioThumbs.slice(1, 4).map((src, i) => (
            <div key={i} className="aspect-square overflow-hidden">
              <img src={src} alt="" loading="lazy" className="w-full h-full object-cover" />
            </div>
          ))}
        </div>
      ) : null}
    </>
  );
}

export function MinimalMiniature({ resolved, content, heroLine, portfolioThumbs }) {
  return (
    <>
      {/* Centered single image, restrained, lots of whitespace */}
      <div className="flex justify-center py-2">
        <div className="aspect-[3/4] w-[55%] overflow-hidden" style={{ background: resolved.muted }}>
          {content.hero_image ? (
            <img src={content.hero_image} alt="" loading="lazy" className="w-full h-full object-cover" />
          ) : null}
        </div>
      </div>
      {/* Tight centered eyebrow + small sans-ish headline */}
      <div className="text-center">
        <div className="text-[9px] uppercase tracking-[0.42em]" style={{ color: resolved.accent, opacity: 0.85 }}>
          {content.hero_eyebrow || "ATELIER"}
        </div>
        <div className="text-base mt-2 leading-snug" style={{ fontFamily: "var(--preview-sans)" }}>
          {heroLine}
        </div>
      </div>
      {/* Strict 3-col grid, square thumbs, equal sizing */}
      {portfolioThumbs.length > 0 ? (
        <div className="grid grid-cols-3 gap-1">
          {portfolioThumbs.slice(0, 3).map((src, i) => (
            <div key={i} className="aspect-square overflow-hidden" style={{ background: resolved.muted }}>
              <img src={src} alt="" loading="lazy" className="w-full h-full object-cover" />
            </div>
          ))}
        </div>
      ) : null}
    </>
  );
}

export function BoldMiniature({ resolved, content, heroLine, portfolioThumbs }) {
  return (
    <>
      {/* Dark canvas, type-led hero — NO photo in the hero, just oversized type */}
      <div
        className="aspect-[16/9] px-4 py-4 flex flex-col justify-end"
        style={{ background: shiftHexLightness(resolved.background, -8) }}
      >
        <div className="text-[9px] uppercase tracking-[0.42em] mb-2" style={{ color: resolved.accent }}>
          {content.hero_eyebrow || "STUDIO"}
        </div>
        <div
          className="text-3xl leading-[0.95] uppercase tracking-tight"
          style={{ fontFamily: "var(--preview-serif)", color: resolved.foreground }}
        >
          {heroLine}
        </div>
      </div>
      {/* Masonry-ish portfolio — uneven heights via aspect ratios */}
      {portfolioThumbs.length > 0 ? (
        <div className="grid grid-cols-3 gap-1.5">
          {portfolioThumbs.slice(0, 4).map((src, i) => {
            const ratios = ["aspect-[3/4]", "aspect-square", "aspect-[4/5]", "aspect-[3/4]"];
            return (
              <div key={i} className={`${ratios[i % ratios.length]} overflow-hidden`}>
                <img src={src} alt="" loading="lazy" className="w-full h-full object-cover" />
              </div>
            );
          })}
        </div>
      ) : null}
    </>
  );
}

export function JournalMiniature({ resolved, content, heroLine, portfolioThumbs }) {
  return (
    <>
      {/* Magazine-style split: image left, caption right */}
      <div className="grid grid-cols-5 gap-3 items-center">
        <div className="col-span-3 aspect-[4/5] overflow-hidden" style={{ background: resolved.muted }}>
          {content.hero_image ? (
            <img src={content.hero_image} alt="" loading="lazy" className="w-full h-full object-cover" />
          ) : null}
        </div>
        <div className="col-span-2 space-y-2">
          <div className="text-[9px] uppercase tracking-[0.32em]" style={{ color: resolved.accent }}>
            {content.hero_eyebrow || "ISSUE · 01"}
          </div>
          <div className="text-lg italic leading-tight" style={{ fontFamily: "var(--preview-serif)" }}>
            {heroLine}
          </div>
          {content.hero_description ? (
            <p className="text-[10px] leading-snug line-clamp-3" style={{ fontFamily: "var(--preview-sans)", opacity: 0.7 }}>
              {content.hero_description}
            </p>
          ) : null}
        </div>
      </div>
      {/* Caption-driven portfolio — each thumb with a tiny meta line */}
      {portfolioThumbs.length > 0 ? (
        <div className="grid grid-cols-2 gap-3">
          {portfolioThumbs.slice(0, 2).map((src, i) => (
            <div key={i} className="space-y-1.5">
              <div className="aspect-[4/3] overflow-hidden" style={{ background: resolved.muted }}>
                <img src={src} alt="" loading="lazy" className="w-full h-full object-cover" />
              </div>
              <div className="text-[9px] uppercase tracking-[0.28em]" style={{ opacity: 0.6 }}>
                Plate {String(i + 1).padStart(2, "0")}
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </>
  );
}

export const TEMPLATE_MINIATURES = {
  editorial: EditorialMiniature,
  minimal: MinimalMiniature,
  bold: BoldMiniature,
  journal: JournalMiniature,
  atelier: AtelierMiniature,
  heirloom: HeirloomMiniature,
  vista: VistaMiniature,
  brutalist: BrutalistMiniature,
  botanical: BotanicalMiniature,
  spotlight: SpotlightMiniature,
};

/* ── v2 template miniatures ─────────────────────────────────────────────
   One miniature per new template. Each lives inside the picker card and
   re-renders with the studio's current content + resolved theme palette,
   so a tenant previews "what would my portfolio look like in Atelier?"
   instead of staring at a static stock thumbnail.

   Visual signature per template:
     · Atelier   — soft framed portrait + italic Playfair + warm rose tint
     · Heirloom  — duotone album spread, hand-set cormorant, paper grain
     · Vista     — wide horizontal hero with horizon line + airy seafoam
     · Brutalist — monospaced caption rail, strict columns, no italics
     · Botanical — botanical hairline + Fraunces wordmark + sage drift
     · Spotlight — poster-headline DM Serif + theatrical dark + gold rule
*/

export function AtelierMiniature({ resolved, content, heroLine, portfolioThumbs }) {
  return (
    <>
      {/* Soft portrait frame — narrow vertical hero, romantic blush wash */}
      <div className="flex justify-center">
        <div className="aspect-[3/4] w-[55%] overflow-hidden relative" style={{ background: resolved.muted }}>
          {content.hero_image ? (
            <img src={content.hero_image} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
          ) : null}
          <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, transparent 50%, ${resolved.background}cc 100%)` }} />
        </div>
      </div>
      <div className="text-center">
        <div className="text-[9px] uppercase tracking-[0.42em]" style={{ color: resolved.accent }}>
          {content.hero_eyebrow || "ATELIER · INTIMATE"}
        </div>
        <div className="text-2xl italic leading-tight mt-2" style={{ fontFamily: "'Playfair Display', serif" }}>
          {heroLine}
        </div>
        <div className="mt-3 inline-block h-px w-12" style={{ background: resolved.accent }} />
      </div>
      {portfolioThumbs.length > 0 ? (
        <div className="grid grid-cols-3 gap-1.5">
          {portfolioThumbs.slice(0, 3).map((src, i) => (
            <div key={i} className="aspect-[3/4] overflow-hidden">
              <img src={src} alt="" loading="lazy" className="w-full h-full object-cover" />
            </div>
          ))}
        </div>
      ) : null}
    </>
  );
}

export function HeirloomMiniature({ resolved, content, heroLine, portfolioThumbs }) {
  return (
    <>
      {/* Two-photo album spread with caramel rule between */}
      <div className="grid grid-cols-2 gap-2">
        {[0, 1].map((i) => (
          <div key={i} className="aspect-[4/5] overflow-hidden" style={{ background: resolved.muted }}>
            {portfolioThumbs[i] || content.hero_image ? (
              <img src={portfolioThumbs[i] || content.hero_image} alt="" loading="lazy" className="w-full h-full object-cover" />
            ) : null}
          </div>
        ))}
      </div>
      <div className="flex items-center gap-3">
        <div className="h-px flex-1" style={{ background: resolved.accent, opacity: 0.4 }} />
        <span className="text-[9px] uppercase tracking-[0.32em]" style={{ color: resolved.accent }}>
          {content.hero_eyebrow || "VOLUME · I"}
        </span>
        <div className="h-px flex-1" style={{ background: resolved.accent, opacity: 0.4 }} />
      </div>
      <div className="text-center text-xl leading-tight" style={{ fontFamily: "var(--preview-serif)" }}>
        {heroLine}
      </div>
    </>
  );
}

export function VistaMiniature({ resolved, content, heroLine, portfolioThumbs }) {
  return (
    <>
      {/* Ultra-wide horizon hero — landscape proportions */}
      <div className="aspect-[21/9] overflow-hidden relative" style={{ background: resolved.muted }}>
        {content.hero_image ? (
          <img src={content.hero_image} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
        ) : null}
        <div className="absolute left-1/2 -translate-x-1/2 bottom-2 h-px w-16" style={{ background: resolved.accent }} />
      </div>
      <div>
        <div className="text-[9px] uppercase tracking-[0.42em]" style={{ color: resolved.accent }}>
          {content.hero_eyebrow || "FIELD · NOTES"}
        </div>
        <div className="text-2xl leading-tight mt-1" style={{ fontFamily: "'Playfair Display', serif" }}>
          {heroLine}
        </div>
      </div>
      {portfolioThumbs.length > 0 ? (
        <div className="grid grid-cols-3 gap-1">
          {portfolioThumbs.slice(0, 3).map((src, i) => (
            <div key={i} className="aspect-[16/10] overflow-hidden">
              <img src={src} alt="" loading="lazy" className="w-full h-full object-cover" />
            </div>
          ))}
        </div>
      ) : null}
    </>
  );
}

export function BrutalistMiniature({ resolved, content, heroLine, portfolioThumbs }) {
  const mono = "'JetBrains Mono', ui-monospace, monospace";
  return (
    <>
      {/* Header rail with monospace nav */}
      <div className="flex items-center justify-between text-[8px] uppercase tracking-[0.32em] pb-1.5 border-b" style={{ borderColor: resolved.accent, color: resolved.accent, fontFamily: mono }}>
        <span>[01]</span>
        <span>{content.studio_name || "STUDIO"}</span>
        <span>{(content.hero_eyebrow || "INDEX").slice(0, 8).toUpperCase()}</span>
      </div>
      {/* Square photo block — no rounded corners, no overlay */}
      <div className="aspect-[16/10] overflow-hidden" style={{ background: shiftHexLightness(resolved.background, -8) }}>
        {content.hero_image ? (
          <img src={content.hero_image} alt="" loading="lazy" className="w-full h-full object-cover" />
        ) : null}
      </div>
      <div className="text-xl leading-tight uppercase tracking-tight" style={{ fontFamily: mono, color: resolved.foreground }}>
        {heroLine}
      </div>
      {/* Strict 4-col grid with hairline borders */}
      {portfolioThumbs.length > 0 ? (
        <div className="grid grid-cols-4 gap-px" style={{ background: resolved.accent, opacity: 0.6 }}>
          {portfolioThumbs.slice(0, 4).map((src, i) => (
            <div key={i} className="aspect-square overflow-hidden" style={{ background: resolved.background }}>
              <img src={src} alt="" loading="lazy" className="w-full h-full object-cover" />
            </div>
          ))}
        </div>
      ) : null}
    </>
  );
}

export function BotanicalMiniature({ resolved, content, heroLine, portfolioThumbs }) {
  return (
    <>
      {/* Garden-press rule with corner ornaments */}
      <div className="flex items-center gap-2">
        <div className="w-3 h-3 rounded-full border" style={{ borderColor: resolved.accent }} />
        <div className="h-px flex-1" style={{ background: resolved.accent, opacity: 0.5 }} />
        <div className="text-[8px] uppercase tracking-[0.42em]" style={{ color: resolved.accent }}>
          {content.hero_eyebrow || "ALMANAC"}
        </div>
        <div className="h-px flex-1" style={{ background: resolved.accent, opacity: 0.5 }} />
        <div className="w-3 h-3 rounded-full border" style={{ borderColor: resolved.accent }} />
      </div>
      <div className="text-2xl text-center leading-tight" style={{ fontFamily: "'Fraunces', serif" }}>
        {heroLine}
      </div>
      <div className="aspect-[4/3] overflow-hidden" style={{ background: resolved.muted }}>
        {content.hero_image ? (
          <img src={content.hero_image} alt="" loading="lazy" className="w-full h-full object-cover" />
        ) : null}
      </div>
      {portfolioThumbs.length > 0 ? (
        <div className="grid grid-cols-3 gap-1.5">
          {portfolioThumbs.slice(0, 3).map((src, i) => (
            <div key={i} className="aspect-square overflow-hidden rounded-sm">
              <img src={src} alt="" loading="lazy" className="w-full h-full object-cover" />
            </div>
          ))}
        </div>
      ) : null}
    </>
  );
}

export function SpotlightMiniature({ resolved, content, heroLine, portfolioThumbs }) {
  return (
    <>
      {/* Theatrical poster — full-bleed dark hero, gold rule, massive type */}
      <div className="aspect-[16/10] relative overflow-hidden" style={{ background: shiftHexLightness(resolved.background, -10) }}>
        {content.hero_image ? (
          <img src={content.hero_image} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-60" />
        ) : null}
        <div className="absolute inset-0" style={{ background: `radial-gradient(ellipse at center, transparent 0%, ${resolved.background} 100%)` }} />
        <div className="absolute inset-x-4 bottom-4">
          <div className="h-px w-12 mb-2" style={{ background: resolved.accent }} />
          <div className="text-[8px] uppercase tracking-[0.42em]" style={{ color: resolved.accent }}>
            {content.hero_eyebrow || "MARQUEE"}
          </div>
          <div className="text-2xl leading-[0.95] mt-1 tracking-tight" style={{ fontFamily: "'DM Serif Display', serif", color: resolved.foreground }}>
            {heroLine}
          </div>
        </div>
      </div>
      {/* Marquee credits row + thumb strip */}
      {portfolioThumbs.length > 0 ? (
        <div className="grid grid-cols-4 gap-1.5">
          {portfolioThumbs.slice(0, 4).map((src, i) => (
            <div key={i} className="aspect-[3/4] overflow-hidden">
              <img src={src} alt="" loading="lazy" className="w-full h-full object-cover" />
            </div>
          ))}
        </div>
      ) : null}
    </>
  );
}

/* ── Surface miniatures ──────────────────────────────────────────────────
   Per-template miniatures that mirror the OTHER public pages a tenant ships
   (Portfolio / Services / Contact). Each template (Editorial / Bold /
   Minimal / Journal) gets its own visual treatment of every surface so a
   studio flipping templates sees the *layout* pivot — not just a relabeled
   editorial sketch on every template. All paint with the resolved palette +
   typography so theme changes still cascade across every template variant.

   Surface miniature contract:
     props: { resolved, content, portfolioThumbs }
     ~3 sketch elements: eyebrow/title + middle composition + closing strip. */

/* ── Portfolio miniatures ────────────────────────────────────────────── */

export function EditorialPortfolioMiniature({ resolved, portfolioThumbs }) {
  // Magazine mosaic — centered chapter eyebrow + serif Portfolio + hero + 3-up.
  const thumbs = portfolioThumbs.length > 0 ? portfolioThumbs : [null, null, null, null];
  return (
    <>
      <div className="text-center">
        <div className="text-[9px] uppercase tracking-[0.32em] mb-2" style={{ color: resolved.accent }}>
          Chapter III · The Work
        </div>
        <div className="mx-auto h-px w-8 mb-3" style={{ background: resolved.accent }} />
        <div className="text-2xl leading-none" style={{ fontFamily: "var(--preview-serif)", fontWeight: 300 }}>
          Portfolio
        </div>
        <div className="text-[9px] uppercase tracking-[0.32em] mt-3" style={{ color: resolved.accent }}>
          A SELECTION · {portfolioThumbs.length || 8} SESSIONS
        </div>
      </div>
      <div className="aspect-[16/9] overflow-hidden" style={{ background: resolved.muted }}>
        {thumbs[0] ? <img src={thumbs[0]} alt="" loading="lazy" className="w-full h-full object-cover" /> : null}
      </div>
      <div className="grid grid-cols-3 gap-1.5">
        {thumbs.slice(1, 4).map((src, i) => (
          <div key={i} className="aspect-[3/4] overflow-hidden" style={{ background: resolved.muted }}>
            {src ? <img src={src} alt="" loading="lazy" className="w-full h-full object-cover" /> : null}
          </div>
        ))}
      </div>
    </>
  );
}

export function BoldPortfolioMiniature({ resolved, portfolioThumbs }) {
  // Oversized uppercase ACT eyebrow + gutter-less masonry tiles with PLATE captions.
  const thumbs = portfolioThumbs.length > 0 ? portfolioThumbs : [null, null, null];
  return (
    <>
      <div>
        <div className="text-[9px] uppercase tracking-[0.42em] mb-1" style={{ color: resolved.accent }}>
          ACT 02 — Recent work
        </div>
        <div
          className="text-3xl uppercase font-extrabold leading-[0.95] tracking-tighter"
          style={{ fontFamily: "var(--preview-sans)" }}
        >
          The Stage
        </div>
      </div>
      {/* Gutter-less tile grid — Bold's signature */}
      <div className="grid grid-cols-12 gap-0">
        <div className="col-span-7 aspect-[16/10] overflow-hidden" style={{ background: shiftHexLightness(resolved.background, -10) }}>
          {thumbs[0] ? <img src={thumbs[0]} alt="" loading="lazy" className="w-full h-full object-cover" /> : null}
        </div>
        <div className="col-span-5 aspect-[3/4] overflow-hidden" style={{ background: shiftHexLightness(resolved.background, -10) }}>
          {thumbs[1] ? <img src={thumbs[1]} alt="" loading="lazy" className="w-full h-full object-cover" /> : null}
        </div>
        <div className="col-span-12 aspect-[16/4] overflow-hidden" style={{ background: shiftHexLightness(resolved.background, -10) }}>
          {thumbs[2] ? <img src={thumbs[2]} alt="" loading="lazy" className="w-full h-full object-cover" /> : null}
        </div>
      </div>
      <div className="flex justify-end text-[9px] uppercase tracking-[0.32em]" style={{ color: resolved.accent }}>
        Open the file →
      </div>
    </>
  );
}

export function MinimalPortfolioMiniature({ resolved, portfolioThumbs }) {
  // Restrained centered count + tiny eyebrow + 3×2 square grid.
  const thumbs = portfolioThumbs.length > 0
    ? [...portfolioThumbs, null, null, null, null, null, null].slice(0, 6)
    : [null, null, null, null, null, null];
  return (
    <>
      <div className="text-center">
        <div className="text-[9px] uppercase tracking-[0.42em]" style={{ color: resolved.accent, opacity: 0.85 }}>
          Recent
        </div>
        <div className="text-base mt-2 leading-snug" style={{ fontFamily: "var(--preview-sans)", opacity: 0.8 }}>
          A small selection.
        </div>
      </div>
      <div className="grid grid-cols-3 gap-1">
        {thumbs.map((src, i) => (
          <div key={i} className="aspect-square overflow-hidden" style={{ background: resolved.muted }}>
            {src ? <img src={src} alt="" loading="lazy" className="w-full h-full object-cover" /> : null}
          </div>
        ))}
      </div>
    </>
  );
}

export function JournalPortfolioMiniature({ resolved, portfolioThumbs }) {
  // Magazine spread — Roman numeral chapters with thumb + caption per row.
  const ROMAN = ["I", "II", "III"];
  const captions = ["Spring Editorial", "Brand Story · Maison V.", "The Wedding of N. & E."];
  const thumbs = portfolioThumbs.length > 0
    ? [...portfolioThumbs, null, null, null].slice(0, 3)
    : [null, null, null];
  return (
    <>
      <div className="flex items-baseline justify-between">
        <div>
          <div className="text-[9px] uppercase tracking-[0.42em]" style={{ color: resolved.accent }}>
            Contents · The Work
          </div>
          <div className="text-2xl italic leading-tight mt-1" style={{ fontFamily: "var(--preview-serif)", fontWeight: 300 }}>
            The Archive
          </div>
        </div>
      </div>
      <ul className="border-y divide-y" style={{ borderColor: shiftHexLightness(resolved.background, -14), divideColor: shiftHexLightness(resolved.background, -14) }}>
        {ROMAN.map((r, i) => (
          <li key={r} className="grid grid-cols-12 gap-2.5 py-2 items-center"
              style={i > 0 ? { borderTop: `1px solid ${shiftHexLightness(resolved.background, -14)}` } : undefined}>
            <div className="col-span-2 aspect-square overflow-hidden" style={{ background: resolved.muted }}>
              {thumbs[i] ? <img src={thumbs[i]} alt="" loading="lazy" className="w-full h-full object-cover" /> : null}
            </div>
            <div className="col-span-10">
              <div className="text-[11px] italic leading-tight" style={{ fontFamily: "var(--preview-serif)" }}>
                <span style={{ color: resolved.accent }}>Chapter {r}</span>
                <span style={{ opacity: 0.4 }}> — </span>
                {captions[i]}
              </div>
              <div className="text-[8px] uppercase tracking-[0.32em] mt-1" style={{ color: resolved.accent, opacity: 0.75 }}>
                MMXXVI · April
              </div>
            </div>
          </li>
        ))}
      </ul>
    </>
  );
}

/* ── Services miniatures ─────────────────────────────────────────────── */

export function EditorialServicesMiniature({ resolved }) {
  const ITEMS = [
    { n: "01", name: "Editorial Portrait", price: "$1,400" },
    { n: "02", name: "Brand Story",        price: "$2,200" },
    { n: "03", name: "Milestone Session",  price: "$3,200" },
  ];
  return (
    <>
      <div>
        <div className="text-[9px] uppercase tracking-[0.32em] mb-2" style={{ color: resolved.accent }}>
          Chapter II · Services
        </div>
        <div className="h-px w-8 mb-3" style={{ background: resolved.accent }} />
        <div className="text-2xl leading-tight" style={{ fontFamily: "var(--preview-serif)", fontWeight: 300 }}>
          A short menu, kept by{" "}
          <em style={{ fontStyle: "italic", color: resolved.accent }}>hand</em>.
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3 pt-2">
        {ITEMS.map((it, i) => (
          <div
            key={it.n}
            className={`pl-3 ${i > 0 ? "border-l" : ""}`}
            style={i > 0 ? { borderLeftColor: shiftHexLightness(resolved.background, -14) } : undefined}
          >
            <div className="text-xl tabular-nums leading-none"
                 style={{ fontFamily: "var(--preview-serif)", color: resolved.accent, opacity: 0.8, fontWeight: 300 }}>
              {it.n}
            </div>
            <div className="text-[11px] leading-tight mt-2" style={{ fontFamily: "var(--preview-serif)" }}>
              {it.name}
            </div>
            <div className="h-px w-5 mt-2 mb-2" style={{ background: resolved.accent }} />
            <div className="text-[10px] italic" style={{ fontFamily: "var(--preview-serif)" }}>
              starting at <span style={{ color: resolved.accent }}>{it.price}</span>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

export function BoldServicesMiniature({ resolved }) {
  // Oversized stacked services with thick accent border-top per row (Bold ACT 03).
  const ITEMS = [
    { name: "Editorial Portrait", price: "$1,400" },
    { name: "Brand Story",        price: "$2,200" },
    { name: "Milestone Session",  price: "$3,200" },
  ];
  return (
    <>
      <div>
        <div className="text-[9px] uppercase tracking-[0.42em] mb-1" style={{ color: resolved.accent }}>
          ACT 03 — Terms
        </div>
        <div
          className="text-3xl uppercase font-extrabold leading-[0.95] tracking-tighter"
          style={{ fontFamily: "var(--preview-sans)" }}
        >
          On Offer
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        {ITEMS.map((it) => (
          <div key={it.name} className="border-t-2 pt-2" style={{ borderColor: resolved.accent }}>
            <div
              className="text-[10px] uppercase font-extrabold leading-tight tracking-tight"
              style={{ fontFamily: "var(--preview-sans)" }}
            >
              {it.name}
            </div>
            <div
              className="text-xl mt-2 font-extrabold tabular-nums"
              style={{ fontFamily: "var(--preview-sans)" }}
            >
              {it.price}
            </div>
            <div className="text-[8px] uppercase tracking-[0.32em] mt-2" style={{ color: resolved.accent }}>
              Reserve →
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

export function MinimalServicesMiniature({ resolved }) {
  // 4-row centered hairline list (Aesop / Hermès menu strip).
  const ITEMS = [
    { name: "Editorial Portrait", price: "from $1,400" },
    { name: "Brand Story",        price: "from $2,200" },
    { name: "Milestone Session",  price: "from $3,200" },
    { name: "Phone Consultation", price: "complimentary" },
  ];
  return (
    <>
      <div className="text-center">
        <div className="text-[9px] uppercase tracking-[0.42em]" style={{ color: resolved.accent, opacity: 0.85 }}>
          The menu
        </div>
      </div>
      <ul className="border-y" style={{ borderColor: shiftHexLightness(resolved.background, -16) }}>
        {ITEMS.map((it, i) => (
          <li
            key={it.name}
            className="flex items-baseline justify-between py-2"
            style={i > 0 ? { borderTop: `1px solid ${shiftHexLightness(resolved.background, -16)}` } : undefined}
          >
            <span className="text-[11px]" style={{ fontFamily: "var(--preview-sans)" }}>
              {it.name}
            </span>
            <span className="text-[10px] italic" style={{ fontFamily: "var(--preview-serif)", opacity: 0.7 }}>
              {it.price}
            </span>
          </li>
        ))}
      </ul>
    </>
  );
}

export function JournalServicesMiniature({ resolved }) {
  // Roman-numeral chapters list (Journal contents style).
  const ROMAN = ["I", "II", "III"];
  const ITEMS = [
    { name: "Editorial Portrait", price: "$1,400" },
    { name: "Brand Story",        price: "$2,200" },
    { name: "Milestone Session",  price: "$3,200" },
  ];
  return (
    <>
      <div>
        <div className="text-[9px] uppercase tracking-[0.42em]" style={{ color: resolved.accent }}>
          Contents · Services
        </div>
        <div className="text-2xl italic leading-tight mt-1" style={{ fontFamily: "var(--preview-serif)", fontWeight: 300 }}>
          On Offer
        </div>
      </div>
      <ul className="border-y" style={{ borderColor: shiftHexLightness(resolved.background, -14) }}>
        {ITEMS.map((it, i) => (
          <li
            key={it.name}
            className="flex items-baseline justify-between py-2"
            style={i > 0 ? { borderTop: `1px solid ${shiftHexLightness(resolved.background, -14)}` } : undefined}
          >
            <span className="text-[11px] italic" style={{ fontFamily: "var(--preview-serif)" }}>
              <span style={{ color: resolved.accent }}>Chapter {ROMAN[i]}</span>
              <span style={{ opacity: 0.4 }}> — </span>
              {it.name}
            </span>
            <span className="text-[10px] italic" style={{ fontFamily: "var(--preview-serif)", color: resolved.accent }}>
              {it.price}
            </span>
          </li>
        ))}
      </ul>
    </>
  );
}

/* ── Contact miniatures ──────────────────────────────────────────────── */

function FormFieldStripes({ resolved, count = 3 }) {
  const labels = ["Your name", "Email", "Message"];
  return (
    <div className="space-y-2.5">
      {labels.slice(0, count).map((label, i) => (
        <div key={label}>
          <div className="text-[8px] uppercase tracking-[0.38em] mb-1" style={{ color: resolved.accent }}>
            {label}
          </div>
          <div className="h-px" style={{ background: shiftHexLightness(resolved.background, -20) }} />
          {i === count - 1 ? (
            <div className="h-px mt-2" style={{ background: shiftHexLightness(resolved.background, -20) }} />
          ) : null}
        </div>
      ))}
    </div>
  );
}

export function EditorialContactMiniature({ resolved, content }) {
  return (
    <>
      <div>
        <div className="text-[9px] uppercase tracking-[0.32em] mb-2" style={{ color: resolved.accent }}>
          Chapter IV · Correspondence
        </div>
        <div className="h-px w-8 mb-3" style={{ background: resolved.accent }} />
        <div className="text-2xl leading-tight" style={{ fontFamily: "var(--preview-serif)", fontWeight: 300 }}>
          Write to us,{" "}
          <em style={{ fontStyle: "italic", color: resolved.accent }}>kindly</em>.
        </div>
      </div>
      <div className="grid grid-cols-[1.1fr_1fr] gap-3 pt-1">
        <div>
          <FormFieldStripes resolved={resolved} />
          <div
            className="inline-flex items-center px-3 h-7 text-[8px] uppercase tracking-[0.32em] mt-3"
            style={{ background: resolved.foreground, color: resolved.background }}
          >
            Post your letter →
          </div>
        </div>
        <div
          className="p-3 border"
          style={{ borderColor: shiftHexLightness(resolved.background, -14), background: resolved.muted }}
        >
          <div className="text-[8px] uppercase tracking-[0.38em]" style={{ color: resolved.accent }}>
            The Studio
          </div>
          <div className="h-px w-5 mt-2 mb-2" style={{ background: resolved.accent }} />
          <div className="text-sm italic leading-tight" style={{ fontFamily: "var(--preview-serif)" }}>
            {content.studio_name || "Your Studio"}
          </div>
          <div className="text-[8px] uppercase tracking-[0.38em] mt-3" style={{ color: resolved.accent }}>
            Office hours
          </div>
          <div className="text-[10px] italic mt-1" style={{ fontFamily: "var(--preview-serif)" }}>
            Tue — Sat / 10:00 — 18:00
          </div>
        </div>
      </div>
    </>
  );
}

export function BoldContactMiniature({ resolved, content }) {
  // Oversized stage-door style; inverted dark CTA block; ACT 04 eyebrow.
  return (
    <>
      <div>
        <div className="text-[9px] uppercase tracking-[0.42em] mb-1" style={{ color: resolved.accent }}>
          ACT 04 — Stage door
        </div>
        <div
          className="text-3xl uppercase font-extrabold leading-[0.95] tracking-tighter"
          style={{ fontFamily: "var(--preview-sans)" }}
        >
          Get in touch.
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <FormFieldStripes resolved={resolved} count={2} />
        <div
          className="p-3"
          style={{ background: resolved.foreground, color: resolved.background }}
        >
          <div className="text-[8px] uppercase tracking-[0.32em]" style={{ color: resolved.accent }}>
            The Studio
          </div>
          <div
            className="text-base mt-2 uppercase font-extrabold leading-tight tracking-tight"
            style={{ fontFamily: "var(--preview-sans)" }}
          >
            {content.studio_name || "Your Studio"}
          </div>
          <div className="text-[8px] uppercase tracking-[0.32em] mt-3 opacity-70">
            Tue — Sat / 10—18
          </div>
        </div>
      </div>
      <div
        className="text-center py-3 text-[10px] uppercase tracking-[0.42em] font-extrabold"
        style={{ background: resolved.accent, color: resolved.primary_foreground || "#fff", fontFamily: "var(--preview-sans)" }}
      >
        Book a sitting →
      </div>
    </>
  );
}

export function MinimalContactMiniature({ resolved, content }) {
  // Single centered column, max whitespace; "Let's keep one for you" CTA.
  return (
    <>
      <div className="text-center">
        <div className="text-[9px] uppercase tracking-[0.42em]" style={{ color: resolved.accent, opacity: 0.85 }}>
          Correspondence
        </div>
        <div className="text-lg italic mt-2" style={{ fontFamily: "var(--preview-serif)", fontWeight: 300 }}>
          Let&apos;s keep one for you.
        </div>
      </div>
      <div className="max-w-[80%] mx-auto">
        <FormFieldStripes resolved={resolved} />
      </div>
      <div className="text-center">
        <div
          className="inline-flex items-center px-4 h-7 text-[8px] uppercase tracking-[0.32em]"
          style={{ background: resolved.foreground, color: resolved.background, fontFamily: "var(--preview-sans)" }}
        >
          Send →
        </div>
        <div className="text-[9px] italic mt-3" style={{ fontFamily: "var(--preview-serif)", opacity: 0.65 }}>
          {content.studio_name || "Your Studio"} · By appointment
        </div>
      </div>
    </>
  );
}

export function JournalContactMiniature({ resolved, content }) {
  // Magazine "Enquiries" two-column with caption-style studio card.
  return (
    <>
      <div>
        <div className="text-[9px] uppercase tracking-[0.42em]" style={{ color: resolved.accent }}>
          Contents · Enquiries
        </div>
        <div className="text-2xl italic leading-tight mt-1" style={{ fontFamily: "var(--preview-serif)", fontWeight: 300 }}>
          File a reservation
        </div>
      </div>
      <div className="grid grid-cols-[1fr_1fr] gap-3">
        <FormFieldStripes resolved={resolved} />
        <div
          className="p-2.5 border"
          style={{ borderColor: shiftHexLightness(resolved.background, -14) }}
        >
          <div className="text-[8px] uppercase tracking-[0.32em]" style={{ color: resolved.accent }}>
            The Ledger
          </div>
          <div className="text-[10px] italic mt-1.5" style={{ fontFamily: "var(--preview-serif)" }}>
            {content.studio_name || "Your Studio"}
          </div>
          <div className="text-[8px] uppercase tracking-[0.32em] mt-2" style={{ color: resolved.accent, opacity: 0.75 }}>
            MMXXVI · Tue — Sat
          </div>
        </div>
      </div>
      <div
        className="text-[9px] italic"
        style={{ fontFamily: "var(--preview-serif)", color: resolved.accent }}
      >
        File reservation →
      </div>
    </>
  );
}

/* ── Dispatch maps — pick the right miniature per surface × template ── */

export const PORTFOLIO_SURFACE_MINIATURES = {
  editorial: EditorialPortfolioMiniature,
  bold:      BoldPortfolioMiniature,
  minimal:   MinimalPortfolioMiniature,
  journal:   JournalPortfolioMiniature,
};

export const SERVICES_SURFACE_MINIATURES = {
  editorial: EditorialServicesMiniature,
  bold:      BoldServicesMiniature,
  minimal:   MinimalServicesMiniature,
  journal:   JournalServicesMiniature,
};

export const CONTACT_SURFACE_MINIATURES = {
  editorial: EditorialContactMiniature,
  bold:      BoldContactMiniature,
  minimal:   MinimalContactMiniature,
  journal:   JournalContactMiniature,
};

export const SURFACE_TABS = [
  { id: "home",      label: "Home" },
  { id: "portfolio", label: "Portfolio" },
  { id: "services",  label: "Services" },
  { id: "contact",   label: "Contact" },
];

export const BrandingLivePreview = memo(function BrandingLivePreview({
  slug, studio_name, tagline, wordmark,
  hero_eyebrow, hero_headline, hero_title_line1, hero_title_line2, hero_description, hero_image,
  about_eyebrow, about_title, about_body,
  portfolio_eyebrow, portfolio_title, portfolio_images,
  logo_url, typeface_pair,
  theme_id, theme_overrides, template_id,
}) {
  const pair = TYPEFACE_PAIRS.find((t) => t.id === (typeface_pair || "cormorant-manrope")) || TYPEFACE_PAIRS[0];
  const tmpl = TEMPLATE_BADGES[template_id || "editorial"] || TEMPLATE_BADGES.editorial;
  // Resolve the full palette using the same helper the Theme tab uses so every
  // field a user edits (theme_id, any single override) cascades into the preview
  // immediately. No partial bindings — preview always reflects the live form state.
  const resolved = resolvePalette(theme_id || DEFAULT_THEME_ID, theme_overrides || {});
  // Compose a single hero copy string from whichever fields the studio is
  // actively editing — `tagline` is the legacy single-line; the new fields
  // (hero_title_line1/2) win when present.
  const heroLine = (hero_title_line1 || hero_title_line2)
    ? `${hero_title_line1 || ""}${hero_title_line2 ? " " + hero_title_line2 : ""}`.trim()
    : (tagline || hero_headline || "Portraits, deliberately made.");
  const portfolioThumbs = Array.isArray(portfolio_images) ? portfolio_images.slice(0, 4) : [];

  // Active surface: home (template-aware miniature) / portfolio / services / contact.
  // Each surface paints with the SAME resolved palette + typeface so the studio
  // can audit color choices across every public page without leaving the form.
  const [surface, setSurface] = useState("home");
  const [copied, setCopied] = useState(false);
  // Viewport toggle — lets studios audition the picked template at phone
  // width without leaving the form. Defaults to "desktop" (the natural
  // miniature scale); flipping to "mobile" clamps the inner body to
  // 390px so they can confirm typography wraps gracefully and the hero
  // composition reads at iPhone width before saving.
  const [viewport, setViewport] = useState("desktop");
  const isMobile = viewport === "mobile";
  const normalizedThumbs = portfolioThumbs.map((img) =>
    typeof img === "string" ? img : (img?.url || img?.src)
  );
  const sharedContent = {
    studio_name, wordmark, logo_url,
    hero_eyebrow, hero_description, hero_image,
  };

  // Build the absolute share URL for the active surface so a click on
  // "copy share link" hands the studio a paste-anywhere URL (instagram bio,
  // email signature, group chat) — not just a relative `/s/...` path.
  const surfacePath = `/s/${slug || "your-studio"}${surface !== "home" ? `/${surface}` : ""}`;
  const shareUrl = (typeof window !== "undefined" ? window.location.origin : "") + surfacePath;

  async function copyShareLink() {
    if (!slug) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Share link copied");
      setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error("Couldn't copy — please copy manually");
    }
  }

  return (
    <div
      className="border overflow-hidden"
      data-testid="branding-live-preview"
      style={{
        // One paint pass — every nested element reads the same CSS var.
        "--preview-serif": pair.serif,
        "--preview-sans": pair.sans,
        background: resolved.background,
        color: resolved.foreground,
        borderColor: shiftHexLightness(resolved.background, -14),
      }}
    >
      <div
        className="px-5 py-3 flex items-center justify-between border-b"
        style={{ borderColor: shiftHexLightness(resolved.background, -10) }}
      >
        <span className="text-[10px] uppercase tracking-[0.32em]" style={{ color: resolved.accent }}>
          Live preview
        </span>
        {/* URL hint doubles as an "open in a new tab" link so studios can
            jump from the in-form miniature straight to the real public page
            for whatever surface is currently active. The ghost "copy" pill
            next to it copies an ABSOLUTE share URL (host + path) so the
            studio can paste it into a bio, email signature, or DM without
            leaving the form. Falls back to a static italic label if there's
            no slug yet (brand-new studio). */}
        {slug ? (
          <div className="inline-flex items-center gap-2">
            <a
              href={surfacePath}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="preview-open-live"
              className="text-[10px] font-serif italic inline-flex items-center gap-1.5 hover:opacity-100 transition-opacity"
              style={{ opacity: 0.7, color: resolved.foreground }}
              title="Open this page in a new tab"
            >
              /s/{slug}{surface !== "home" ? `/${surface}` : ""}
              <LinkIcon className="w-3 h-3" strokeWidth={1.5} style={{ color: resolved.accent }} />
            </a>
            <button
              type="button"
              onClick={copyShareLink}
              data-testid="preview-copy-share"
              className="inline-flex items-center gap-1 h-5 px-1.5 text-[9px] uppercase tracking-[0.22em] border transition-colors hover:opacity-100"
              style={{
                opacity: copied ? 1 : 0.55,
                color: copied ? resolved.accent : resolved.foreground,
                borderColor: shiftHexLightness(resolved.background, -16),
                background: "transparent",
              }}
              title="Copy share link"
            >
              {copied ? (
                <>
                  <Check className="w-2.5 h-2.5" strokeWidth={2} /> Copied
                </>
              ) : (
                <>
                  <Copy className="w-2.5 h-2.5" strokeWidth={1.5} /> Copy
                </>
              )}
            </button>
          </div>
        ) : (
          <span className="text-[10px] font-serif italic" style={{ opacity: 0.6 }}>
            /s/your-studio{surface !== "home" ? `/${surface}` : ""}
          </span>
        )}
      </div>

      {/* Surface tab strip — flips the preview between Home / Portfolio /
          Services / Contact so studios can audit palette + typography across
          every public page, not just the hero miniature. */}
      <div
        className="px-3 pt-3 pb-2 border-b flex items-center gap-2"
        style={{ borderColor: shiftHexLightness(resolved.background, -10) }}
        data-testid="preview-surface-tabs"
      >
        <div className="flex-1 flex items-center gap-0.5">
          {SURFACE_TABS.map((t) => {
            const active = surface === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setSurface(t.id)}
                data-testid={`preview-surface-tab-${t.id}`}
                className="flex-1 h-7 text-[9px] uppercase tracking-[0.22em] transition-colors"
                style={
                  active
                    ? { background: resolved.accent, color: resolved.primary_foreground || "#fff" }
                    : { color: resolved.foreground, opacity: 0.55, background: "transparent" }
                }
              >
                {t.label}
              </button>
            );
          })}
        </div>
        {/* Desktop ↔ Mobile viewport toggle — clamps the inner preview body
            to 390px when active so studios can sanity-check phone wrapping
            without leaving the admin form. */}
        <div
          className="shrink-0 inline-flex items-center border h-7"
          style={{ borderColor: shiftHexLightness(resolved.background, -10) }}
          role="group"
          aria-label="Preview viewport"
          data-testid="preview-viewport-toggle"
        >
          {[
            { id: "desktop", Icon: Monitor, label: "Desktop" },
            { id: "mobile", Icon: Smartphone, label: "Mobile" },
          ].map(({ id, Icon, label }) => {
            const active = viewport === id;
            return (
              <button
                key={id}
                type="button"
                onClick={() => setViewport(id)}
                aria-pressed={active}
                title={`${label} preview`}
                data-testid={`preview-viewport-${id}`}
                className="px-2 h-full inline-flex items-center justify-center transition-colors"
                style={
                  active
                    ? { background: resolved.accent, color: resolved.primary_foreground || "#fff" }
                    : { color: resolved.foreground, opacity: 0.55, background: "transparent" }
                }
              >
                <Icon className="w-3 h-3" strokeWidth={1.75} />
              </button>
            );
          })}
        </div>
      </div>

      {/* Mobile-clamp wrapper — when viewport === "mobile" the preview body
          is constrained to ~290px wide (a small-phone breakpoint, narrower
          than the natural preview pane so the clamp is visibly meaningful
          even when the sidebar itself is already phone-ish) and centered.
          Desktop mode keeps the natural full-width miniature. The clamp
          doesn't change any internal markup — Tailwind's responsive
          utilities inside each Miniature re-flow naturally at the
          narrower container width. */}
      <div
        className={isMobile ? "mx-auto transition-[max-width] duration-200" : "transition-[max-width] duration-200"}
        style={isMobile ? { maxWidth: "290px" } : undefined}
        data-testid={`preview-viewport-container-${viewport}`}
      >

      <div className="p-6 space-y-5">
        {surface === "home" ? (
          <>
            {/* Template badge */}
            <div className="flex items-center justify-between text-[9px] uppercase tracking-[0.32em] -mt-1" data-testid="preview-template-badge">
              <span style={{ color: resolved.accent }}>{tmpl.label}</span>
              <span className="font-serif italic normal-case tracking-normal" style={{ opacity: 0.5 }}>· {tmpl.note}</span>
            </div>

            {/* Template-specific miniature — the heart of the preview. Switching
                `template_id` swaps the entire central composition (hero layout,
                typography rhythm, portfolio arrangement). Palette, fonts, and
                content all bind through to whichever miniature is active. */}
            {(() => {
              const Miniature = TEMPLATE_MINIATURES[template_id] || TEMPLATE_MINIATURES.editorial;
              return (
                <div data-testid={`preview-miniature-${template_id || "editorial"}`} className="space-y-5">
                  <Miniature
                    resolved={resolved}
                    pair={pair}
                    heroLine={heroLine}
                    portfolioThumbs={normalizedThumbs}
                    content={sharedContent}
                  />
                </div>
              );
            })()}

            {/* CTA — common to all templates, styled with resolved primary */}
            <button
              type="button"
              className="px-4 h-9 text-[10px] uppercase tracking-[0.28em]"
              style={{ background: resolved.primary, color: resolved.primary_foreground }}
              data-testid="preview-cta"
            >
              Book a session
            </button>

            {/* About snippet — only renders when the studio has actually written one */}
            {(about_eyebrow || about_title || about_body) ? (
              <div className="pt-2 border-t" style={{ borderColor: shiftHexLightness(resolved.background, -8) }}>
                {about_eyebrow ? (
                  <div className="text-[10px] uppercase tracking-[0.32em] mb-1" style={{ color: resolved.accent }}>
                    {about_eyebrow}
                  </div>
                ) : null}
                {about_title ? (
                  <div className="text-base italic" style={{ fontFamily: "var(--preview-serif)" }}>
                    {about_title}
                  </div>
                ) : null}
                {about_body ? (
                  <p className="text-xs mt-1 line-clamp-2 leading-relaxed" style={{ opacity: 0.7 }}>
                    {about_body}
                  </p>
                ) : null}
              </div>
            ) : null}
          </>
        ) : null}

        {surface === "portfolio" ? (
          <div data-testid="preview-surface-portfolio" className="space-y-5">
            {(() => {
              const M = PORTFOLIO_SURFACE_MINIATURES[template_id] || PORTFOLIO_SURFACE_MINIATURES.editorial;
              return <M resolved={resolved} content={sharedContent} portfolioThumbs={normalizedThumbs} />;
            })()}
          </div>
        ) : null}

        {surface === "services" ? (
          <div data-testid="preview-surface-services" className="space-y-5">
            {(() => {
              const M = SERVICES_SURFACE_MINIATURES[template_id] || SERVICES_SURFACE_MINIATURES.editorial;
              return <M resolved={resolved} content={sharedContent} />;
            })()}
          </div>
        ) : null}

        {surface === "contact" ? (
          <div data-testid="preview-surface-contact" className="space-y-4">
            {(() => {
              const M = CONTACT_SURFACE_MINIATURES[template_id] || CONTACT_SURFACE_MINIATURES.editorial;
              return <M resolved={resolved} content={sharedContent} />;
            })()}
          </div>
        ) : null}

        {/* Resolved palette swatches — every override + theme change reflects here */}
        <div className="pt-2 border-t" style={{ borderColor: shiftHexLightness(resolved.background, -8) }}>
          <div className="text-[10px] uppercase tracking-[0.22em] mb-2" style={{ opacity: 0.6 }}>
            Palette
          </div>
          <div className="flex gap-1.5" data-testid="preview-palette-swatches">
            {[
              { c: resolved.background, label: "background" },
              { c: resolved.foreground, label: "foreground" },
              { c: resolved.primary, label: "primary" },
              { c: resolved.accent, label: "accent" },
              { c: resolved.muted, label: "muted" },
            ].map(({ c, label }) => (
              <div key={label}
                   className="w-9 h-9 border"
                   style={{ background: c, borderColor: shiftHexLightness(resolved.background, -14) }}
                   title={`${label} · ${c}`} />
            ))}
          </div>
        </div>

        {/* Type pairing */}
        <div className="pt-2 border-t text-[11px]" style={{ borderColor: shiftHexLightness(resolved.background, -8), opacity: 0.7 }}>
          <span className="italic" style={{ fontFamily: "var(--preview-serif)" }}>{pair.serifLabel}</span>
          <span className="mx-2 opacity-50">×</span>
          <span className="tracking-wide" style={{ fontFamily: "var(--preview-sans)" }}>{pair.sansLabel}</span>
        </div>
      </div>
      </div>
    </div>
  );
});

export function HeroImageUploader({ value, onChange, studioName, focalX = 50, focalY = 50, onFocalChange }) {
  /* Polished hero-image uploader designed to match the EXACT public-page rendering
     so what you see here is what /s/{slug} will show. Three core states:
       1. Empty — big drop-zone with file picker, drag-drop, and a URL-paste popover.
       2. Set   — live 4:5 preview (matches the tenant Home hero ratio) with
                  hover controls (Replace · URL · Remove), a draggable focal-point
                  crosshair so the studio can pick what the public hero centers on
                  (CSS `object-position: X% Y%`), and a meta strip showing file
                  size + dimensions for freshly uploaded files.
       3. Uploading — spinner overlay with progress messaging.

     The recommended dimensions (1600×2000, 4:5) and 8 MB cap are surfaced inline
     so photographers know what to upload. */
  const inputRef = useRef(null);
  const replaceRef = useRef(null);
  const previewRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [showUrl, setShowUrl] = useState(false);
  const [pasteUrl, setPasteUrl] = useState("");
  const [meta, setMeta] = useState(null); // {size_kb, width, height}
  const [draggingFocal, setDraggingFocal] = useState(false);

  const hasImage = !!value;

  async function uploadFile(file) {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      return toast.error("Please choose an image file (.jpg, .png, .webp)");
    }
    if (file.size > 15 * 1024 * 1024) {
      return toast.error("Hero image must be under 15 MB");
    }
    setUploading(true);
    try {
      // Read width/height locally before upload so we can show meta immediately
      // and warn if the photo isn't the recommended portrait orientation.
      const dims = await readImageDimensions(file);
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      setMeta({
        size_kb: Math.round(file.size / 102.4) / 10,
        width: dims.width,
        height: dims.height,
      });
      if (dims.width > dims.height) {
        toast.warning("This image is landscape — the hero crops to portrait (4:5). Consider a taller crop.");
      } else {
        toast.success("Hero image uploaded");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
      if (replaceRef.current) replaceRef.current.value = "";
    }
  }

  function applyUrl() {
    const url = pasteUrl.trim();
    if (!url) return;
    onChange(url);
    setMeta(null);
    setShowUrl(false);
    setPasteUrl("");
    toast.success("Hero image URL applied");
  }

  function clearImage() {
    if (!window.confirm("Remove the hero image? The public page will fall back to the placeholder.")) return;
    onChange("");
    setMeta(null);
    toast("Hero cleared");
  }

  function onDrop(e) {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) uploadFile(file);
  }

  /* Compute a focal (x%, y%) from a pointer event over the preview box and
     forward to the parent. Clamped to [0, 100] so a slightly-off drag during
     fast mouse moves can't push the focal off-frame. No-ops if the parent
     didn't pass `onFocalChange` (e.g. older callers). */
  function setFocalFromEvent(e) {
    if (!onFocalChange || !previewRef.current) return;
    const r = previewRef.current.getBoundingClientRect();
    const px = ((e.clientX - r.left) / r.width) * 100;
    const py = ((e.clientY - r.top) / r.height) * 100;
    const clamp = (n) => Math.max(0, Math.min(100, n));
    onFocalChange(Math.round(clamp(px) * 10) / 10, Math.round(clamp(py) * 10) / 10);
  }

  function onFocalPointerDown(e) {
    if (!onFocalChange) return;
    e.preventDefault();
    setDraggingFocal(true);
    setFocalFromEvent(e);
    previewRef.current?.setPointerCapture?.(e.pointerId);
  }
  function onFocalPointerMove(e) {
    if (!draggingFocal) return;
    setFocalFromEvent(e);
  }
  function onFocalPointerUp(e) {
    if (!draggingFocal) return;
    setDraggingFocal(false);
    try { previewRef.current?.releasePointerCapture?.(e.pointerId); } catch (_err) { /* harmless if not captured */ }
  }
  function resetFocal(e) {
    e.stopPropagation();
    onFocalChange?.(50, 50);
  }

  return (
    <div className="space-y-3" data-testid="hero-image-uploader">
      <div className="flex items-baseline justify-between gap-2">
        <span className="eyebrow">Hero image</span>
        <span className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
          Portrait · 4:5 · 1600×2000 recommended
        </span>
      </div>

      {hasImage ? (
        /* ----- SET STATE ----- */
        <div
          ref={previewRef}
          onPointerDown={onFocalPointerDown}
          onPointerMove={onFocalPointerMove}
          onPointerUp={onFocalPointerUp}
          onPointerCancel={onFocalPointerUp}
          className={`group relative aspect-[4/5] bg-muted overflow-hidden border border-border ${onFocalChange ? "cursor-crosshair touch-none" : ""}`}
          data-testid="hero-image-preview"
        >
          <img
            src={value}
            alt="Hero preview"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ objectPosition: `${focalX}% ${focalY}%` }}
            draggable={false}
            data-testid="hero-preview-image"
          />
          {/* Focal-point crosshair — draggable target showing where the
              public hero will center. Hidden if the parent didn't pass an
              onFocalChange (legacy callers). */}
          {onFocalChange && (
            <div
              className="absolute pointer-events-none"
              style={{
                left: `${focalX}%`,
                top: `${focalY}%`,
                transform: "translate(-50%, -50%)",
              }}
              data-testid="hero-focal-marker"
            >
              {/* Outer halo (always visible during drag) */}
              <div
                className={`w-11 h-11 rounded-full border-2 ${draggingFocal ? "border-[#C9A961] bg-[#C9A961]/20" : "border-white/90 bg-black/15"} shadow-[0_0_0_1px_rgba(0,0,0,0.45)] transition-colors`}
              />
              {/* Inner dot */}
              <div
                className={`absolute top-1/2 left-1/2 w-2 h-2 rounded-full -translate-x-1/2 -translate-y-1/2 ${draggingFocal ? "bg-[#C9A961]" : "bg-white"} shadow-[0_0_0_1px_rgba(0,0,0,0.55)]`}
              />
              {/* Crosshair lines */}
              <span className="absolute top-1/2 left-1/2 w-9 h-px bg-white/70 -translate-x-1/2 -translate-y-1/2" />
              <span className="absolute top-1/2 left-1/2 h-9 w-px bg-white/70 -translate-x-1/2 -translate-y-1/2" />
            </div>
          )}
          {uploading && (
            <div className="absolute inset-0 bg-black/60 flex items-center justify-center" data-testid="hero-uploading">
              <Loader2 className="w-6 h-6 text-white animate-spin" />
            </div>
          )}
          {/* Hover toolbar */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/0 via-black/0 to-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-between p-3 pointer-events-none">
            <div className="text-xs text-white/90 max-w-[60%] truncate" title={value}>
              {value.startsWith("http") ? new URL(value, window.location.origin).pathname : value}
            </div>
            <div className="flex items-center gap-1 pointer-events-auto" onPointerDown={(e) => e.stopPropagation()}>
              <input
                ref={replaceRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => uploadFile(e.target.files?.[0])}
                data-testid="hero-replace-input"
              />
              {onFocalChange && (focalX !== 50 || focalY !== 50) && (
                <button
                  type="button"
                  onClick={resetFocal}
                  title="Reset focal point to center"
                  data-testid="hero-focal-reset"
                  className="w-8 h-8 flex items-center justify-center bg-white/90 text-stone-900 hover:bg-white"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                </button>
              )}
              <button
                type="button"
                onClick={() => replaceRef.current?.click()}
                title="Replace image"
                data-testid="hero-replace-btn"
                className="w-8 h-8 flex items-center justify-center bg-white/90 text-stone-900 hover:bg-white"
              >
                <RefreshCw className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                onClick={() => setShowUrl((v) => !v)}
                title="Paste URL"
                data-testid="hero-url-toggle"
                className="w-8 h-8 flex items-center justify-center bg-white/90 text-stone-900 hover:bg-white"
              >
                <LinkIcon className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                onClick={clearImage}
                title="Remove image"
                data-testid="hero-remove-btn"
                className="w-8 h-8 flex items-center justify-center bg-rose-600/90 text-white hover:bg-rose-700"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
          {/* Top-left helper strip: live coordinates while interacting, route hint otherwise */}
          <div
            className="absolute top-2 left-2 px-2 py-1 bg-black/60 text-white text-[10px] uppercase tracking-[0.18em] opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
            data-testid="hero-focal-coord"
          >
            {onFocalChange
              ? `Focal · ${Math.round(focalX)}% · ${Math.round(focalY)}%`
              : `Live preview · matches /${studioName ? studioName.toLowerCase().replace(/\s+/g, "-") : "your-slug"}`}
          </div>
        </div>
      ) : (
        /* ----- EMPTY STATE ----- */
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          data-testid="hero-dropzone"
          className={`group block w-full aspect-[4/5] border-2 border-dashed transition-colors ${dragging ? "border-foreground bg-muted/40" : "border-border hover:border-foreground/60 bg-muted/20"} flex flex-col items-center justify-center gap-3 text-center px-6`}
        >
          {uploading ? (
            <>
              <Loader2 className="w-8 h-8 animate-spin text-foreground" />
              <div className="text-sm">Uploading your hero image…</div>
            </>
          ) : (
            <>
              <div className="w-14 h-14 border border-border flex items-center justify-center group-hover:border-foreground/60 transition-colors">
                <ImagePlus className="w-6 h-6 text-muted-foreground group-hover:text-foreground" />
              </div>
              <div>
                <div className="font-medium">Add your hero image</div>
                <div className="text-xs text-muted-foreground mt-1">
                  Drop a photo here or click to browse
                </div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mt-3">
                  PNG, JPG, or WebP · up to 15 MB
                </div>
              </div>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setShowUrl(true); }}
                className="text-xs text-muted-foreground underline hover:text-foreground mt-2"
                data-testid="hero-paste-url-link"
              >
                or paste an image URL
              </button>
            </>
          )}
        </button>
      )}

      {/* Hidden file input — used by both empty-state click and the Replace button */}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => uploadFile(e.target.files?.[0])}
        data-testid="hero-file-input"
      />

      {/* URL paste row — toggled from empty state and hover toolbar */}
      {showUrl && (
        <div className="flex items-center gap-2" data-testid="hero-url-row">
          <input
            value={pasteUrl}
            onChange={(e) => setPasteUrl(e.target.value)}
            placeholder="https://example.com/hero.jpg"
            className="flex-1 bg-transparent border-b border-border py-2 outline-none focus:border-foreground text-sm"
            data-testid="hero-url-input"
          />
          <button
            type="button"
            onClick={applyUrl}
            className="px-3 h-9 text-xs uppercase tracking-[0.18em] bg-foreground text-background"
            data-testid="hero-url-apply"
          >
            Apply
          </button>
          <button
            type="button"
            onClick={() => { setShowUrl(false); setPasteUrl(""); }}
            className="w-9 h-9 flex items-center justify-center border border-border"
            aria-label="Cancel"
            data-testid="hero-url-cancel"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* Meta strip — only shown for fresh uploads where we know dimensions */}
      {meta && (
        <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.22em] text-muted-foreground border-t border-border pt-2" data-testid="hero-meta">
          <span>{meta.width} × {meta.height}px</span>
          <span>{meta.size_kb < 1024 ? `${meta.size_kb} KB` : `${(meta.size_kb / 1024).toFixed(1)} MB`}</span>
          {meta.width > meta.height && (
            <span className="text-amber-600">Landscape · will crop</span>
          )}
        </div>
      )}

      <p className="text-xs text-muted-foreground">
        Tip: a vertical (portrait) photo of your subject or workspace works best — the hero crops to 4:5.
        {onFocalChange && " Click anywhere on the preview above to set the focal point — that's the spot the public hero will always keep in view, even on narrow mobile screens."}
      </p>
    </div>
  );
}

/* Read width/height from a File so we can warn about wrong-aspect uploads BEFORE
   the server takes the bytes. Avoids a server round-trip just to read dimensions. */
function readImageDimensions(file) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
      URL.revokeObjectURL(url);
    };
    img.onerror = () => {
      resolve({ width: 0, height: 0 });
      URL.revokeObjectURL(url);
    };
    img.src = url;
  });
}

export function ImageUploader({ value, onChange, testid }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  async function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please choose an image");
    if (file.size > 15 * 1024 * 1024) return toast.error("Image larger than 15 MB");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  return (
    <div className="flex items-center gap-2">
      <input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Paste URL or upload →"
        className="flex-1 bg-transparent border-b border-border py-2 outline-none focus:border-foreground text-sm"
        data-testid={`${testid}-url`}
      />
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handleFile} data-testid={`${testid}-file`} />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        className="inline-flex items-center gap-2 px-3 h-9 text-xs uppercase tracking-[0.18em] border border-border hover:border-foreground/60 disabled:opacity-60"
        data-testid={`${testid}-upload-btn`}
      >
        {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
        {uploading ? "Uploading…" : "Upload"}
      </button>
    </div>
  );
}

/* Stable empty array reference used when portfolio_images is undefined —
   prevents PortfolioEditor's memo from busting on every parent render. */
export const EMPTY_IMAGE_LIST = Object.freeze([]);
export const EMPTY_FOCAL_LIST = Object.freeze([]);

/* PortfolioEditor — memoized so it only re-renders when the image list,
   focal-point list, or onChange refs change. Caller passes:
     images         — current image URLs
     focalPoints    — current focal-point array (parallel to images)
     onChange       — (nextImages) => void
     onFocalChange  — (nextFocalPoints) => void
   All four move together via the local mutators (upload/remove/move) which
   keep the focal-points array in strict lockstep with the images array. */
export const PortfolioEditor = memo(function PortfolioEditor({
  images: imagesProp,
  focalPoints: focalsProp,
  onChange,
  onFocalChange,
}) {
  const images = imagesProp || EMPTY_IMAGE_LIST;
  const focals = focalsProp || EMPTY_FOCAL_LIST;
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [pasteUrl, setPasteUrl] = useState("");

  // Every mutation goes through commit() so images + focals stay lockstep.
  const commit = (nextImages, nextFocals) => {
    onChange(nextImages);
    if (onFocalChange) onFocalChange(nextFocals);
  };

  async function uploadFiles(files) {
    setUploading(true);
    try {
      const next = [...images];
      const nextFocals = [...focals];
      const autoFocalTasks = [];  // (index, url) tuples — fire AFTER commit
      for (const file of files) {
        if (!file.type.startsWith("image/")) continue;
        if (file.size > 15 * 1024 * 1024) {
          toast.error(`${file.name} is larger than 15 MB — skipped`);
          continue;
        }
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/admin/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        next.push(data.url);
        nextFocals.push({ x: 50, y: 50 });
        autoFocalTasks.push({ index: next.length - 1, url: data.url });
      }
      commit(next, nextFocals);
      toast.success(`${files.length} image${files.length > 1 ? "s" : ""} added`);

      // Fire auto-focal in the background — silent, best-effort. Each
      // completed detection splices its own {x,y} into the focals array
      // and re-commits, so upload UX never blocks on Gemini's ~5s latency.
      autoFocalTasks.forEach(({ index, url }) => {
        api.post("/admin/site/auto-focal", { image_url: url })
          .then(({ data }) => {
            if (!data?.detected) return;   // no face → keep center default
            setFocal(index, { x: data.x, y: data.y });
          })
          .catch(() => {
            /* silent — center default is a fine fallback */
          });
      });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  function addUrl() {
    if (!pasteUrl.trim()) return;
    commit([...images, pasteUrl.trim()], [...focals, { x: 50, y: 50 }]);
    setPasteUrl("");
  }

  function remove(idx) {
    commit(images.filter((_, i) => i !== idx), focals.filter((_, i) => i !== idx));
  }

  function move(idx, dir) {
    const next = [...images];
    const nextF = [...focals];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    [nextF[idx], nextF[target]] = [nextF[target], nextF[idx]];
    commit(next, nextF);
  }

  function setFocal(idx, nextFocal) {
    const nextF = [...focals];
    while (nextF.length <= idx) nextF.push({ x: 50, y: 50 });
    nextF[idx] = nextFocal;
    if (onFocalChange) onFocalChange(nextF);
  }

  /* Manual per-tile "Auto-focus" — user clicks the sparkles button on a
     tile and we ask Gemini to detect the primary face. Unlike the on-upload
     path (silent, best-effort), THIS path shows a loading state + toast on
     success/failure because the studio explicitly asked for it. */
  async function autoFocalFor(idx, src) {
    const toastId = toast.loading("Detecting focal point…");
    try {
      const { data } = await api.post("/admin/site/auto-focal", { image_url: src });
      if (data?.detected) {
        setFocal(idx, { x: data.x, y: data.y });
        toast.success("Focal point set to detected face.", { id: toastId });
      } else {
        toast.info("No clear face found — kept the current point.", { id: toastId });
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Auto-focus failed.", { id: toastId });
    }
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row gap-3">
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => uploadFiles(Array.from(e.target.files || []))}
          data-testid="portfolio-file-input"
        />
        <button
          type="button"
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
          className="inline-flex items-center gap-2 px-5 h-10 border border-[color:var(--gold-base)]/40 hover:border-[color:var(--gold-base)] hover:bg-[color:var(--gold-base)]/5 text-[10px] uppercase tracking-[0.28em] font-serif italic disabled:opacity-60 transition-colors"
          data-testid="portfolio-upload-btn"
        >
          {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin not-italic" /> : <span className="text-[color:var(--gold-base)] not-italic">+</span>}
          <span className="not-italic">{uploading ? "Uploading…" : "Add an image"}</span>
        </button>

        <div className="flex flex-1 gap-2">
          <input
            value={pasteUrl}
            onChange={(e) => setPasteUrl(e.target.value)}
            placeholder="…or paste an image URL"
            className="flex-1 bg-transparent border-b border-border py-2 outline-none focus:border-foreground text-sm"
            data-testid="portfolio-paste-url"
          />
          <button
            type="button"
            onClick={addUrl}
            className="inline-flex items-center gap-1 px-3 h-10 text-xs uppercase tracking-[0.18em] border border-border hover:border-foreground/60"
            data-testid="portfolio-add-url-btn"
          >
            <Plus className="w-3.5 h-3.5" /> Add
          </button>
        </div>
      </div>

      {images.length === 0 ? (
        <div className="border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
          No photos yet. Upload images or paste URLs to start your gallery.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="portfolio-list">
          {images.map((src, i) => (
            <PortfolioTile
              key={`${src}-${i}`}
              src={src}
              index={i}
              focal={focals[i]}
              onFocalChange={(next) => setFocal(i, next)}
              onAutoFocal={() => autoFocalFor(i, src)}
              onMoveUp={() => move(i, -1)}
              onMoveDown={() => move(i, 1)}
              onRemove={() => remove(i)}
            />
          ))}
        </div>
      )}

      <p className="text-xs text-muted-foreground">
        Photos appear in the order shown. <strong>Click any photo</strong> to set the focal point — or use the <Sparkles className="inline w-3 h-3 -mt-0.5" /> button to let AI find the primary face. Auto-detect also runs on every new upload. Don&rsquo;t forget to click <strong>Save changes</strong>.
      </p>
    </div>
  );
});

/* PortfolioTile — extracted + memoized.
   Why a separate component:
     1. React.memo with stable equality (src + index) means reordering ONE
        tile doesn't re-render its siblings — only the two that swapped.
     2. The hover overlay is now opacity-only on a composited layer
        (will-change: opacity). Previous version animated background-color
        via `transition-colors`, which forces a paint on every mouseenter/leave
        — measured at ~700ms/hover on Edge/Windows 4x CPU. Opacity is GPU-
        composited and free.
     3. The tile itself is promoted to its own composite layer
        (translateZ(0)), so its hover state never invalidates siblings'
        layers — the browser only recomposites the ONE tile.
     4. No content-visibility: at 8-20 items it's pure overhead (the browser
        runs visibility checks on every paint without ever skipping anything).
        Only valuable past ~30 tiles, in which case we'd add it conditionally. */
const PortfolioTile = memo(function PortfolioTile({ src, index, focal, onFocalChange, onAutoFocal, onMoveUp, onMoveDown, onRemove }) {
  const fx = clampPct(focal?.x, 50);
  const fy = clampPct(focal?.y, 50);
  const tileRef = useRef(null);
  const [autoBusy, setAutoBusy] = useState(false);

  const handleClick = (e) => {
    // Ignore clicks on the hover-control buttons themselves — they're the
    // ↑ ↓ ✕ ✨ icons living inside the same tile.
    if (e.target.closest("[data-focal-skip]")) return;
    const rect = tileRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    onFocalChange?.({ x: clampPct(x, 50), y: clampPct(y, 50) });
  };

  const handleAuto = async () => {
    if (autoBusy || !onAutoFocal) return;
    setAutoBusy(true);
    try {
      await onAutoFocal();
    } finally {
      setAutoBusy(false);
    }
  };

  return (
    <div
      ref={tileRef}
      onClick={handleClick}
      role="button"
      title="Click to set focal point"
      className="group relative aspect-[4/5] bg-muted overflow-hidden border border-border cursor-crosshair"
      style={{ transform: "translateZ(0)" }}
      data-testid={`portfolio-tile-${index}`}
    >
      <img
        src={src}
        alt={`portfolio ${index + 1}`}
        loading="lazy"
        decoding="async"
        draggable={false}
        className="w-full h-full object-cover pointer-events-none select-none"
        style={{ objectPosition: `${fx}% ${fy}%` }}
      />
      {/* Focal-point ring marker — always visible so studios can see what's
          currently set without hovering. Ignored by the tile-level click
          handler thanks to pointer-events:none. */}
      <span
        className="absolute pointer-events-none rounded-full border-2 border-white shadow-md ring-1 ring-black/50"
        style={{
          left: `${fx}%`,
          top: `${fy}%`,
          transform: "translate(-50%, -50%)",
          width: 14,
          height: 14,
          background: "rgba(0,0,0,0.55)",
        }}
        data-testid={`focal-marker-${index}`}
      />
      {/* Hover overlay — opacity-only transition on a composited layer. */}
      <div
        className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-150 flex items-center justify-center gap-1"
        style={{ willChange: "opacity" }}
      >
        <span data-focal-skip title="Auto-detect subject with AI">
          <IconBtn
            label={autoBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            onClick={handleAuto}
            testid={`portfolio-autofocal-${index}`}
          />
        </span>
        <span data-focal-skip>
          <IconBtn label="↑" onClick={onMoveUp} testid={`portfolio-up-${index}`} />
        </span>
        <span data-focal-skip>
          <IconBtn label="↓" onClick={onMoveDown} testid={`portfolio-down-${index}`} />
        </span>
        <span data-focal-skip>
          <IconBtn label={<X className="w-3.5 h-3.5" />} onClick={onRemove} testid={`portfolio-remove-${index}`} destructive />
        </span>
      </div>
      <div className="absolute top-1.5 left-1.5 text-[10px] bg-background/80 px-1.5 py-0.5">{index + 1}</div>
    </div>
  );
});

function clampPct(n, fallback) {
  const v = Number(n);
  if (!Number.isFinite(v)) return fallback;
  if (v < 0) return 0;
  if (v > 100) return 100;
  return v;
}

export function IconBtn({ label, onClick, testid, destructive }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`w-8 h-8 flex items-center justify-center text-xs ${destructive ? "bg-destructive text-destructive-foreground" : "bg-background text-foreground"}`}
    >
      {label}
    </button>
  );
}


export function CopyField({ label, value, testid }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Couldn't copy — select and copy manually");
    }
  }
  return (
    <div className="flex items-stretch gap-2" data-testid={testid}>
      <div className="flex-1 min-w-0 px-3 py-2 bg-muted/30 border border-border text-sm font-mono text-foreground/90 overflow-x-auto whitespace-nowrap">
        {value}
      </div>
      <button
        type="button"
        onClick={copy}
        className="px-3 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] bg-foreground text-background hover:opacity-90 transition-opacity"
        data-testid={`${testid}-copy`}
        aria-label={`Copy ${label}`}
      >
        {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
        {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
}

export const SUGGESTED_THEMES = {
  editorial: { typeface_pair: "cormorant-manrope", accent_hex: "#C9A961", accent_label: "champagne gold" },
  minimal:   { typeface_pair: "cormorant-manrope", accent_hex: "#9CAF88", accent_label: "sage" },
  bold:      { typeface_pair: "ppeditorial-sohne", accent_hex: "#0E0D0C", accent_label: "obsidian" },
  journal:   { typeface_pair: "canela-gtamerica",  accent_hex: "#C8997E", accent_label: "desert" },
  // ── v2 templates (mixed niche + aesthetic). Each maps to a theme_id +
  // typeface pair so picking the template in the admin auto-applies the
  // companion mood, while the studio can still tune accent/typeface freely
  // afterwards. ──────────────────────────────────────────────────────────
  atelier:   { typeface_pair: "playfair-inter",   accent_hex: "#A18066", accent_label: "rose gold", theme_id: "rose-gold" },
  heirloom:  { typeface_pair: "cormorant-manrope", accent_hex: "#B8967D", accent_label: "caramel",  theme_id: "warm-boutique" },
  vista:     { typeface_pair: "playfair-inter",   accent_hex: "#5EAAA8", accent_label: "seafoam",   theme_id: "coastal-blue" },
  brutalist: { typeface_pair: "jetbrains-inter",  accent_hex: "#F59E0B", accent_label: "signal amber", theme_id: "bold-dark" },
  botanical: { typeface_pair: "fraunces-manrope", accent_hex: "#7D9E68", accent_label: "garden sage", theme_id: "sage-garden" },
  spotlight: { typeface_pair: "dmserif-inter",    accent_hex: "#C9A961", accent_label: "champagne gold", theme_id: "midnight" },
};

export const ACCENT_PRESETS = [
  { id: "rose",      label: "rose gold",      hex: "#D4A5A5" },
  { id: "midnight",  label: "midnight",       hex: "#1A1F3A" },
  { id: "sage",      label: "sage",           hex: "#9CAF88" },
  { id: "mauve",     label: "mauve",          hex: "#A78BAA" },
  { id: "obsidian",  label: "obsidian",       hex: "#0E0D0C" },
  { id: "desert",    label: "desert",         hex: "#C8997E" },
  { id: "champagne", label: "champagne gold", hex: "#C9A961" },
];

export function AccentColorPicker({ s, set }) {
  const current = (s.theme_overrides?.primary || "#C9A961").toLowerCase();
  const presetMatch = ACCENT_PRESETS.find((p) => p.hex.toLowerCase() === current);
  const customRef = useRef(null);

  function applyHex(hex) {
    const overrides = { ...(s.theme_overrides || {}), primary: hex };
    set("theme_overrides", overrides);
  }

  return (
    <div className="space-y-3" data-testid="branding-accent-picker">
      <span className="block text-[10px] uppercase tracking-[0.32em] text-[color:var(--gold-base)]">
        Accent color
      </span>
      <div className="grid grid-cols-4 sm:grid-cols-8 gap-3 max-w-lg">
        {ACCENT_PRESETS.map((p) => {
          const active = presetMatch?.id === p.id;
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => applyHex(p.hex)}
              data-testid={`branding-accent-${p.id}`}
              className="flex flex-col items-center gap-1.5 group"
              aria-pressed={active}
              aria-label={p.label}
            >
              <span
                className={`relative w-8 h-8 rounded-full transition-all ${active ? "ring-2 ring-[color:var(--gold-base)] ring-offset-2 ring-offset-background" : "ring-1 ring-border group-hover:ring-foreground/40"}`}
                style={{ background: p.hex }}
              >
                {active && (
                  <Check className="absolute inset-0 m-auto w-3.5 h-3.5 text-[#FAF9F6] mix-blend-difference" strokeWidth={2.5} />
                )}
              </span>
              <span className="text-[10px] font-serif italic text-muted-foreground/80 leading-none">
                {p.label}
              </span>
            </button>
          );
        })}

        {/* Custom — opens native color picker */}
        <button
          type="button"
          onClick={() => customRef.current?.click()}
          className="flex flex-col items-center gap-1.5 group"
          data-testid="branding-accent-custom"
          aria-label="Custom color"
        >
          <span
            className={`relative w-8 h-8 rounded-full border border-dashed flex items-center justify-center ${!presetMatch ? "ring-2 ring-[color:var(--gold-base)] ring-offset-2 ring-offset-background border-[color:var(--gold-base)]" : "border-border group-hover:border-foreground/40"}`}
            style={{ background: !presetMatch ? current : "transparent" }}
          >
            {presetMatch && (
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground">+</span>
            )}
          </span>
          <span className="text-[10px] font-serif italic text-muted-foreground/80 leading-none">
            custom
          </span>
          <input
            ref={customRef}
            type="color"
            value={current}
            onChange={(e) => applyHex(e.target.value)}
            className="sr-only"
            data-testid="branding-accent-custom-input"
          />
        </button>
      </div>
      <p className="text-[11px] font-serif italic text-muted-foreground/70 max-w-md">
        The studio's signature color — flows through every per-tenant site template via <span className="not-italic">--tenant-accent</span>.
      </p>
    </div>
  );
}


/* end of _shared.jsx — extracted from Site.jsx on 2026-06-26 */
